import React from "react";
import { Card, CardBody, Container, Row, Col, Table } from "reactstrap";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { Line, Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  BarElement,
} from "chart.js";
import AnalyticsNavigation from "./components/AnalyticsNavigation";
import { authenticatedGet } from "../../utils/authenticatedFetch";

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const UserAnalytics = () => {
  const [userData, setUserData] = React.useState(null);
  const [retentionData, setRetentionData] = React.useState(null);
  const [engagementData, setEngagementData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // Fetch all data using the authenticated fetch utility
        const [userDataJson, retentionDataJson, engagementDataJson] =
          await Promise.all([
            authenticatedGet("/api/admin/analytics/users"),
            authenticatedGet("/api/admin/analytics/users/retention"),
            authenticatedGet("/api/admin/analytics/users/engagement"),
          ]);

        if (
          userDataJson.success &&
          retentionDataJson.success &&
          engagementDataJson.success
        ) {
          setUserData(userDataJson.data);
          setRetentionData(retentionDataJson.data);
          setEngagementData(engagementDataJson.data);
        } else {
          throw new Error("Failed to fetch user analytics data");
        }
      } catch (err) {
        setError(err.message);
        console.error("Error fetching user analytics data:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Prepare retention data for chart
  const retentionChartData = {
    labels: retentionData ? retentionData.map((week) => week.period) : [],
    datasets: [
      {
        label: "Retention Rate (%)",
        data: retentionData
          ? retentionData.map((week) => week.retentionRate)
          : [],
        borderColor: "rgb(53, 162, 235)",
        backgroundColor: "rgba(53, 162, 235, 0.5)",
      },
      {
        label: "Active Users",
        data: retentionData
          ? retentionData.map((week) => week.activeUsers)
          : [],
        borderColor: "rgb(255, 99, 132)",
        backgroundColor: "rgba(255, 99, 132, 0.5)",
      },
    ],
  };

  // Prepare daily activity data for chart
  const dailyActivityData = {
    labels: engagementData
      ? engagementData.userActivity.dailyActivity.map((day) => day.day)
      : [],
    datasets: [
      {
        label: "User Activity",
        data: engagementData
          ? engagementData.userActivity.dailyActivity.map((day) => day.count)
          : [],
        backgroundColor: "rgba(75, 192, 192, 0.6)",
      },
    ],
  };

  // Prepare hourly activity data for chart
  const hourlyActivityData = {
    labels: engagementData
      ? engagementData.userActivity.hourlyActivity.map(
          (hour) => `${hour.hour}:00`
        )
      : [],
    datasets: [
      {
        label: "Hourly Activity",
        data: engagementData
          ? engagementData.userActivity.hourlyActivity.map((hour) => hour.count)
          : [],
        backgroundColor: "rgba(153, 102, 255, 0.6)",
      },
    ],
  };

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs title="Analytics" breadcrumbItem="User Analytics" />
        <AnalyticsNavigation activeTab="users" />

        {loading && (
          <div className="text-center mt-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        )}

        {error && (
          <Card>
            <CardBody>
              <div className="alert alert-danger mb-0">
                Error loading user analytics data: {error}
              </div>
            </CardBody>
          </Card>
        )}

        {userData && retentionData && engagementData && (
          <>
            <Row>
              <Col xl={3} md={6}>
                <Card>
                  <CardBody>
                    <div className="d-flex">
                      <div className="flex-grow-1">
                        <h4 className="fw-medium">
                          {userData.totalUsers.toLocaleString()}
                        </h4>
                        <p className="text-muted mb-0">Total Users</p>
                      </div>
                      <div className="avatar-sm rounded-circle bg-primary align-self-center mini-stat-icon">
                        <span className="avatar-title rounded-circle bg-primary">
                          <i className="ri-user-line font-size-24"></i>
                        </span>
                      </div>
                    </div>
                  </CardBody>
                </Card>
              </Col>

              <Col xl={3} md={6}>
                <Card>
                  <CardBody>
                    <div className="d-flex">
                      <div className="flex-grow-1">
                        <h4 className="fw-medium">
                          {userData.dailyActiveUsers.toLocaleString()}
                        </h4>
                        <p className="text-muted mb-0">DAU</p>
                      </div>
                      <div className="avatar-sm rounded-circle bg-success align-self-center mini-stat-icon">
                        <span className="avatar-title rounded-circle bg-success">
                          <i className="ri-user-follow-line font-size-24"></i>
                        </span>
                      </div>
                    </div>
                  </CardBody>
                </Card>
              </Col>

              <Col xl={3} md={6}>
                <Card>
                  <CardBody>
                    <div className="d-flex">
                      <div className="flex-grow-1">
                        <h4 className="fw-medium">
                          {userData.monthlyActiveUsers.toLocaleString()}
                        </h4>
                        <p className="text-muted mb-0">MAU</p>
                      </div>
                      <div className="avatar-sm rounded-circle bg-info align-self-center mini-stat-icon">
                        <span className="avatar-title rounded-circle bg-info">
                          <i className="ri-team-line font-size-24"></i>
                        </span>
                      </div>
                    </div>
                  </CardBody>
                </Card>
              </Col>

              <Col xl={3} md={6}>
                <Card>
                  <CardBody>
                    <div className="d-flex">
                      <div className="flex-grow-1">
                        <h4 className="fw-medium">
                          {userData.retentionRate.toFixed(2)}%
                        </h4>
                        <p className="text-muted mb-0">Retention Rate</p>
                      </div>
                      <div className="avatar-sm rounded-circle bg-warning align-self-center mini-stat-icon">
                        <span className="avatar-title rounded-circle bg-warning">
                          <i className="ri-repeat-line font-size-24"></i>
                        </span>
                      </div>
                    </div>
                  </CardBody>
                </Card>
              </Col>
            </Row>

            <Row>
              <Col xl={6}>
                <Card>
                  <CardBody>
                    <h4 className="card-title mb-4">User Retention (Weekly)</h4>
                    <div style={{ height: "300px", position: "relative" }}>
                      <Line
                        data={retentionChartData}
                        options={{
                          maintainAspectRatio: false,
                          responsive: true,
                          plugins: {
                            legend: { position: "top" },
                          },
                        }}
                      />
                    </div>
                  </CardBody>
                </Card>
              </Col>

              <Col xl={6}>
                <Card>
                  <CardBody>
                    <h4 className="card-title mb-4">Daily User Activity</h4>
                    <div style={{ height: "300px", position: "relative" }}>
                      <Bar
                        data={dailyActivityData}
                        options={{
                          maintainAspectRatio: false,
                          responsive: true,
                          plugins: {
                            legend: { position: "top" },
                          },
                        }}
                      />
                    </div>
                  </CardBody>
                </Card>
              </Col>
            </Row>

            <Row>
              <Col xl={12}>
                <Card>
                  <CardBody>
                    <h4 className="card-title mb-4">Hourly User Activity</h4>
                    <div style={{ height: "300px", position: "relative" }}>
                      <Bar
                        data={hourlyActivityData}
                        options={{
                          maintainAspectRatio: false,
                          responsive: true,
                          plugins: {
                            legend: { position: "top" },
                          },
                        }}
                      />
                    </div>
                  </CardBody>
                </Card>
              </Col>
            </Row>

            <Row>
              <Col xl={12}>
                <Card>
                  <CardBody>
                    <h4 className="card-title mb-4">
                      Top Users by Trading Volume
                    </h4>
                    <div className="table-responsive">
                      <Table className="table-striped mb-0">
                        <thead>
                          <tr>
                            <th>#</th>
                            <th>User ID</th>
                            <th>Username</th>
                            <th>Email</th>
                            <th>Trading Volume</th>
                            <th>Trade Count</th>
                          </tr>
                        </thead>
                        <tbody>
                          {engagementData.topUsers.map((user, index) => (
                            <tr key={user.userId}>
                              <th scope="row">{index + 1}</th>
                              <td>{user.userId}</td>
                              <td>{user.username || "N/A"}</td>
                              <td>{user.email}</td>
                              <td>${user.tradingVolume.toFixed(2)}</td>
                              <td>{user.tradeCount}</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </>
        )}
      </Container>
    </div>
  );
};

export default UserAnalytics;
